import * as Yup from "yup"
import moment from "moment-timezone"
import {
  transformSnakeToCamel,
  transformCamelToSnake,
  camelToSnake,
} from "utils/transform"
import { onRemoveEmptyKeys } from "utils/objectUtils"

// Yup Schemas
export const DateRangeSchema = Yup.object().shape({
  startDate: Yup.date().nullable(),
  endDate: Yup.date().nullable(),
})

export const PaginationSchema = Yup.object().shape({
  page: Yup.number().min(1).default(1),
  limit: Yup.number().min(1).default(10),
})

export const SortSchema = Yup.object().shape({
  field: Yup.string().required(),
  order: Yup.string().oneOf(["asc", "desc"]).default("desc"),
})

export const PayloadSchema = Yup.object()
  .transform(transformSnakeToCamel)
  .shape({
    searchTerm: Yup.string().nullable(),
    dateRange: DateRangeSchema.nullable(),
    pagination: PaginationSchema.nullable(),
    sort: SortSchema.nullable(),
    filters: Yup.object().nullable(),
    status: Yup.string().nullable(),
    category: Yup.string().nullable(),
  })

export default class PayloadModel {
  /**
   * Transform json to model
   * @param {object} json - JSON data to transform
   * @returns {object} Transformed model
   */
  static jsonToModel(json) {
    try {
      const model = PayloadSchema.cast(json, {
        abortEarly: false,
        stripUnknown: true,
      })
      return model
    } catch (error) {
      throw error
    }
  }

  /**
   * Transform model to json
   * @param {object} model - Model to transform
   * @param {string} format - Output format ('nested' or 'flat')
   * @param {boolean} removeEmptyKeys - Whether to remove empty keys
   * @returns {object} Transformed JSON
   */
  static modelToJson(model, format = "flat", removeEmptyKeys = true) {
    const json = {}

    // COMMON
    for (const key in model) {
      if (model[key] && typeof model[key] !== "object") {
        json[camelToSnake(key)] = model[key]
      }
    }

    // DATE RANGE
    if (
      model.dateRange &&
      (model.dateRange.startDate || model.dateRange.endDate)
    ) {
      // Normalize range to cover whole days in Asia/Bangkok, then convert to UTC
      const TZ = "Asia/Bangkok"
      const start = model.dateRange.startDate
        ? moment(model.dateRange.startDate).tz(TZ).startOf("day").utc().toISOString()
        : undefined
      const end = model.dateRange.endDate
        ? moment(model.dateRange.endDate).tz(TZ).endOf("day").utc().toISOString()
        : undefined

      if (format === "flat") {
        if (start) json.start_date = start
        if (end) json.end_date = end
      } else {
        json.date_range = {}
        if (start) json.date_range.start_date = start
        if (end) json.date_range.end_date = end
      }
    }

    // SORT
    if (model.sort) {
      if (format === "flat") {
        json.sort_field = model.sort.field
        json.sort_order = model.sort.order
      } else {
        json.sort = {
          field: model.sort.field,
          order: model.sort.order,
        }
      }
    }

    // FILTERS
    if (model.filters) {
      if (format === "flat") {
        json.filters = Object.entries(model.filters)
          .flatMap(([key, values]) =>
            values.map(
              value =>
                `[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`
            )
          )
          .join("&")
      } else {
        json.filters = model.filters
      }
    }

    // SEARCH
    if (model.search) {
      if (format === "flat") {
        json.search = Object.entries(model.search)
          .flatMap(
            ([key, value]) =>
              `[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`
          )
          .join("&")
      } else {
        json.search = model.search
      }
    }

    // PAGINATION
    if (model.pagination) {
      if (format === "flat") {
        json.page = model.pagination.page
        json.limit = model.pagination.limit
      } else {
        json.pagination = {
          page: model.pagination.page,
          limit: model.pagination.limit,
        }
      }
    }

    // Remove empty keys if needed
    if (removeEmptyKeys) {
      return transformCamelToSnake(onRemoveEmptyKeys(json))
    }

    return transformCamelToSnake(json)
  }
}

/*
// Example Decode
const filters = {
  "performance.createdLinks": ["CCC", "DDD"],
  "performance.seller": ["AAA", "BBB"]
};

const queryString = Object.entries(filters)
  .flatMap(([key, values]) => 
    values.map(value => `filter[${encodeURIComponent(key)}]=${encodeURIComponent(value)}`)
  )
  .join("&");

const url = `/products?${queryString}`;
console.log(url);
*/
